import React, { FC, createContext, useContext, useEffect, useState, useCallback } from 'react';
// @fenderdigital
import isDomUsable from '@fenderdigital/utils/dom';
import Portal from '@fenderdigital/ui/Portal';
// components
import NonUpgradableModal from '../components/NonUpgradableModal';
// lib
import Cookie from '../lib/cookie';
// constants
import FALLBACK_APPROVED_COUNTRIES_LIST from '../constants/fallback-countries-list';
// core
import bugsnagClient from '../core/bugsnag';
import { getCountriesFromRemoteConfig } from '../core/firebase';

interface CountriesContextValue {
  approvedCountries?: string[] | null;
  nonApprovedCountries?: string[] | null;
  countryCode: string;
  error?: string | null;

  hideNonUpgradableModal: () => void;
  isLoadingCountries?: boolean;
  isNonApprovedCountry: boolean;
  setCountryCodeValue: (value: string) => void;
  showNonUpgradableModal: () => void;
}

const CountriesContext = createContext<CountriesContextValue>({
  approvedCountries: null,
  nonApprovedCountries: null,
  countryCode: '',
  error: null,
  hideNonUpgradableModal: () => {},
  isLoadingCountries: false,
  isNonApprovedCountry: false,
  setCountryCodeValue: () => {},
  showNonUpgradableModal: () => {},
});

export const useCountriesContext = (): CountriesContextValue => useContext(CountriesContext);

const modalRoot = isDomUsable() && document.querySelector('#modal-root');

const CountriesProvider: FC = ({ children }) => {
  const [approvedCountries, setApprovedCountries] = useState<string[] | null>(null);
  const [nonApprovedCountries, setNonApprovedCountries] = useState<string[] | null>(null);
  const [isNonUpgradableModalVisible, setIsNonUpgradableModalVisible] = useState<boolean>(false);
  const [isNonApprovedCountry, setIsNonApprovedCountry] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<string>(Cookie.getCountry());
  const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // fetches the remote config for approved countries
  const handleGetCountries = useCallback(async (): Promise<void> => {
    setIsLoadingCountries(true);
    try {
      const config = await getCountriesFromRemoteConfig();
      setApprovedCountries(config.approvedCountries);
      setNonApprovedCountries(config.nonApprovedCountries);
      setIsNonApprovedCountry(!config.approvedCountries?.includes(countryCode.toUpperCase()));
    } catch (err) {
      // retry remote config fetch on error or timeout
      getCountriesFromRemoteConfig()
        .then(config => {
          setApprovedCountries(config.approvedCountries);
          setNonApprovedCountries(config.nonApprovedCountries);
          setIsNonApprovedCountry(!config.approvedCountries?.includes(countryCode.toUpperCase()));
        })
        .catch(retryErr => {
          setError(retryErr.message);
          // if it fails on retry use the local country list
          // this should insure we always have a list to verify
          setApprovedCountries(FALLBACK_APPROVED_COUNTRIES_LIST);
          setIsNonApprovedCountry(
            !FALLBACK_APPROVED_COUNTRIES_LIST.includes(countryCode.toUpperCase()),
          );
          bugsnagClient.notify(
            new Error(
              // eslint-disable-next-line max-len
              'Firebase Remote Config: Countries retry fetch failed and is using the static fallback list',
            ),
          );
        });
      bugsnagClient.notify(new Error('Firebase Remote Config: Initial Countries fetch failed'));
    } finally {
      setIsLoadingCountries(false);
    }
  }, [countryCode]);

  useEffect(() => {
    handleGetCountries();
  }, [handleGetCountries]);

  const setCountryCodeValue = async (value: string): Promise<void> => {
    await Cookie.setCountry(value);
    setCountryCode(value);
  };

  const hideNonUpgradableModal = (): void => {
    setIsNonUpgradableModalVisible(false);
  };

  const showNonUpgradableModal = (): void => {
    setIsNonUpgradableModalVisible(true);
  };

  const ctxVal: CountriesContextValue = {
    approvedCountries,
    nonApprovedCountries,
    countryCode,
    error,
    hideNonUpgradableModal,
    isLoadingCountries,
    isNonApprovedCountry,
    setCountryCodeValue,
    showNonUpgradableModal,
  };
  return (
    <CountriesContext.Provider value={ctxVal}>
      {children}
      {isNonUpgradableModalVisible && (
        <Portal root={modalRoot as HTMLElement}>
          <NonUpgradableModal onClose={hideNonUpgradableModal} />
        </Portal>
      )}
    </CountriesContext.Provider>
  );
};

export default CountriesProvider;
